/**
 * Business-owner-facing explainers for the public /demo-business/scenarios
 * runner.  Strict wording rules:
 *  - Product-truth only ("ConfirmPH can…" / "…helps you…").
 *  - No sales-agent language ("say this to the owner", "close the deal", etc.).
 *  - Keep each block short: the scenario itself does the teaching visually.
 *  - Do NOT overstate what ConfirmPH does (e.g. "makes double bookings
 *    impossible" is banned; "detect conflicts between recorded bookings"
 *    is correct).
 */

export const OWNER_EXPLAINERS = {
  // ---- Booking intake -------------------------------------------------
  "messenger-inquiry": {
    what: "A customer sends a Messenger inquiry. Details are captured into the ConfirmPH Notepad, promoted into a booking draft, and then confirmed with a shareable link.",
    why:  "You can continue receiving bookings through Messenger. ConfirmPH helps turn the important details into a structured booking record so nothing is lost between chats.",
    tryIt: "Tap through the chat, record it in the Notepad, then send the confirmation link back to the customer.",
  },
  "whatsapp-inquiry": {
    what: "The same flow, but the inquiry comes through WhatsApp instead of Messenger.",
    why:  "Different app, same record. Every booking still ends up in one place with the same fields, source-tagged as WhatsApp.",
  },
  "sms-inquiry": {
    what: "A short SMS inquiry becomes a complete booking with the same intake flow.",
    why:  "Even brief text messages can become well-structured bookings without extra tools.",
  },
  "phone-call": {
    what: "A customer calls the shop. You type the details straight into the ConfirmPH Notepad while on the call.",
    why:  "Phone-only inquiries can still get one written record instead of a paper slip.",
  },
  "walk-in": {
    what: "A customer walks into the shop. The employee opens ConfirmPH on the office computer and captures the request in the Notepad.",
    why:  "Walk-in customers can be recorded through the same structured booking process: no separate workflow to remember.",
  },

  // ---- Booking problems ----------------------------------------------
  "double-booking": {
    what: "Two bookings for the same vehicle are recorded with overlapping dates.",
    why:  "ConfirmPH can detect conflicts between bookings recorded in the system so they can be reviewed before pickup day.",
    tryIt: "Add the second inquiry, look at the overlap warning, then reassign the second booking to an available vehicle.",
  },
  "tight-buffer": {
    what: "One booking ends only a short time before another begins on the same vehicle.",
    why:  "Tight turnaround times can be identified in advance so staff know cleaning and inspection windows are short.",
  },
  "customer-date-change": {
    what: "A customer asks to move their booking to different dates. You edit the booking, ConfirmPH re-runs its conflict check, and a fresh confirmation link is sent.",
    why:  "Dates can be updated on the booking record without losing the customer's original request or re-typing anything into Messenger.",
    tryIt: "Open Grace's booking from Follow-ups, edit the return date, and send the fresh link.",
  },
  "awaiting-confirmation": {
    what: "A confirmation link was sent to the customer but they haven't confirmed yet. It shows up in Follow-ups automatically.",
    why:  "Pending confirmations stay visible so it's easier to follow up before the pickup day.",
    tryIt: "Send Ruth's follow-up from the Follow-ups screen, then watch the booking auto-update.",
  },
  "forgotten-messenger-booking": {
    what: "A customer arrives at the counter claiming a booking that was never entered into ConfirmPH. You search for it, don't find it, and recover it into the Notepad on the spot.",
    why:  "ConfirmPH can only warn you about bookings it can see. Recording inquiries as they arrive keeps them from being forgotten between chats.",
    tryIt: "Search for Ella, then recover her booking into the Notepad now.",
  },

  // ---- Vehicle issues ------------------------------------------------
  "vehicle-breakdown": {
    what: "A vehicle is reported down with damage evidence. An upcoming booking depends on it.",
    why:  "Vehicle issues can be connected to upcoming bookings so staff can see what may be affected and plan a response.",
    tryIt: "Tap Report breakdown on the Kia Rio, review the affected booking, then assign an available replacement.",
  },
  "vehicle-maintenance": {
    what: "You schedule a maintenance window on a specific vehicle. Overlapping bookings get flagged in the Warning Center.",
    why:  "Blocked-out periods are visible against the booking calendar so future inquiries don't accidentally overlap.",
    tryIt: "Open the Almera, set its service window, then resolve the flagged draft.",
  },
  "late-return": {
    what: "A rental is overdue and the next booking is about to start.",
    why:  "Overdue returns can be surfaced against the next booking so staff know which upcoming pickup is at risk.",
  },
  "replacement-vehicle": {
    what: "A vehicle needs to be swapped mid-booking or before pickup.",
    why:  "The booking record can be updated with the new vehicle without losing the original customer's request or dates.",
  },
  "damage-incident": {
    what: "Damage found on a returning vehicle is logged with photo evidence. Future bookings on the same vehicle are surfaced automatically.",
    why:  "Incident records stay tied to the specific booking and vehicle so the history is not spread across chats.",
    tryIt: "Open the return-inspection booking, log the incident with the photo, then reassign the affected upcoming booking.",
  },

  // ---- Staff & daily ops --------------------------------------------
  "follow-up-reminder": {
    what: "A follow-up reminder appears in the Follow-ups screen. Sending it also lands on the booking's activity log.",
    why:  "Reminders stay visible in one place, and every action is attributed to the staff account that took it.",
    tryIt: "Send David's follow-up, then open the booking's activity log to see who did what.",
  },
  "multi-staff-activity": {
    what: "Multiple staff worked on the same booking during the week. The activity log records who did what and when.",
    why:  "You get a clear who-did-what timeline per booking without comparing chat screenshots.",
    tryIt: "Open the Hiace booking's activity log to see every recorded action attributed to a specific staff account.",
  },
  "upcoming-pickup": {
    what: "The Follow-ups screen surfaces upcoming pickups. Vehicle-readiness checklists live in the same product.",
    why:  "You can run the pickup-prep checklist against the exact booking, so prep is timestamped and attributed.",
    tryIt: "Open the pre-pickup reminder, start the Vehicle Readiness checklist, and submit it.",
  },
  "pickup-handover": {
    what: "A last reminder fires an hour before pickup, then odometer, fuel, condition and keys are recorded against the booking right before the customer drives off.",
    why:  "The handover check becomes part of the booking record: the same record both sides can point back to if a condition question comes up later.",
    tryIt: "Open the booking, start the pickup handover, record the handover, then open the return workflow.",
  },
  "return-and-ready": {
    what: "The same handover check runs again at return. Once it's logged, the booking moves to Completed and the vehicle resets to Available.",
    why:  "The full lifecycle, from pickup to return to the vehicle being ready again: stays on one record instead of scattered notes.",
    tryIt: "Complete the return inspection, open the vehicle record, then continue once it is Available again.",
  },
  "new-inquiry-alert": {
    what: "The vehicle is back to Available, and right on cue a new booking inquiry comes in for it.",
    why:  "The same notification surface staff already use for reminders is what surfaces new inquiries too: nothing new to learn.",
    tryIt: "Open the new inquiry to complete this lifecycle and reach the closing video.",
  },

  // ---- Financial -----------------------------------------------------
  "deposit-balance": {
    what: "A booking records the total price, the deposit paid, and the remaining balance.",
    why:  "Keep booking financial records clearer without relying only on chat history or memory.",
  },

  // ---- Trust / presence ---------------------------------------------
  "verified-review": {
    what: "A review comes from a booking that was actually completed on ConfirmPH.",
    why:  "Reviews tied to real completed bookings can help build customer trust for future inquiries.",
  },
  "business-page": {
    what: "This is the free public rental profile every ConfirmPH business gets. Customers can browse the fleet, check availability, and send a booking request.",
    why:  "Bookings submitted here land directly inside your Booking Intake: no shared inbox to sort through.",
    tryIt: "Pick a vehicle, choose dates, check availability and send a request.",
  },
  "public-website": {
    what: "This is the multi-page ConfirmPH Website your rental business can have: Home, Fleet, Booking, About, Policies, FAQ, Reviews and Contact: powered by your ConfirmPH data.",
    why:  "You get a professional rental website without needing a separate web developer, hosting or CMS.",
    tryIt: "Tap through the site tabs and switch between Phone and Desktop to see how it looks on each.",
  },

  // ---- Owner & Operations --------------------------------------------
  "employees-management": {
    what: "Only the business owner sees the Employees page. You invite staff with an email/username + password, tick the permissions they need, and edit them any time.",
    why:  "Employees only see and act on what you allow. Every action they take is attributable back to their account.",
    tryIt: "Add Gina as Booking Staff with 'Bookings' + 'Reminders' permission, then open her performance dashboard.",
  },
  "staff-availability": {
    what: "The 'Who's on the clock' page shows who is online, busy or on break. Inquiries and bookings route to a clocked-in staff member with the right permission: the owner is the fallback.",
    why:  "The clock-in state plus permissions decide where new inquiries land. Attendance history is on the same page.",
    tryIt: "Look at the online/offline lists, then follow how the new inquiry ends up on Ana's activity log.",
  },
  "vehicles-management": {
    what: "The Vehicles page lists your fleet. The Add Vehicle form is tabbed: Basic Info, Technical, Pricing, Public Description, Rules & Pickup, Internal Notes, Visibility, so you can control exactly what each customer sees.",
    why:  "Fleet capacity is measured in points per vehicle category. You pick a plan that fits the size of your fleet.",
    tryIt: "Add a Honda BR-V and watch the fleet-capacity meter update.",
  },
  "daily-checklists": {
    what: "Daily Checklists live in ConfirmPH. Opening, closing, vehicle readiness, cleaning, handover and fuel-charge templates come pre-seeded and are editable per business.",
    why:  "Missed opening or closing days surface a red banner the next morning, so nothing quietly gets skipped.",
    tryIt: "Run the Opening: shop checklist, then see it saved with the employee and timestamp attached.",
  },
  "warnings-compliance": {
    what: "The Warning Center is the owner's cockpit for anything currently going wrong: late returns, unpaid deposits, missing return inspections, buffer clashes and more.",
    why:  "Every warning has a real primary action attached. Reassign, notify the customer, review, dismiss or add a note.",
    tryIt: "Open the critical late-return warning and reassign the next booking's vehicle.",
  },
};
